import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import TooltipedIcon from '../../../common/TooltipedIcon'
import { toast } from '../../../common/Toast'
import { DownloadIcon, InformationIcon } from '../../../../icons'
import { downloadFileUTF8, getLastNYearsRangeISO, sortModelsAlphabetically } from '../../../../utils/functions'
import countries from '../../../../utils/countries.json'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadModels, loadRegions } from '../../../../actions/shared'
import {
  clearReportsMessages,
  downloadGeographicalConnectionData,
  loadGeographicalConnectionSummaryData,
  loadConnectedDevicesCountries,
} from '../../../../actions/reports'
import GeographicalConnectionChart from './GeographicalConnectionChart'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'region',
    order: 'asc',
    translationKey: 'region_asc',
  },
  {
    field: 'region',
    order: 'desc',
    translationKey: 'region_desc',
  },
  {
    field: 'country',
    order: 'asc',
    translationKey: 'country_asc',
  },
  {
    field: 'country',
    order: 'desc',
    translationKey: 'country_desc',
  },
  {
    field: 'total',
    order: 'asc',
    translationKey: 'count_asc',
  },
  {
    field: 'total',
    order: 'desc',
    translationKey: 'count_desc',
  },
]

const { startISO: defaultStartISO, endISO: defaultEndISO, timeZone } = getLastNYearsRangeISO(5)

const GeographicalConnectionReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [geographicalConnectionSummaryData, setGeographicalConnectionSummaryData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [startDateFilter, setStartDateFilter] = useState(defaultStartISO)
  const [endDateFilter, setEndDateFilter] = useState(defaultEndISO)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [noPing, setNoPing] = useState(false)
  const [expandedDevice, setExpandedDevice] = useState(0)
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const UNKNOWN_LABEL = t('unknown', { defaultValue: 'Unknown' })
  const hasTable = geographicalConnectionSummaryData.length > 0

  const isUnknownCountryCode = (code) => code == null || code === '' || code === 'ZZ'
  const getCountryName = (code) => countries.find((c) => c.code === code)?.name || code
  const getCountryDisplay = (code) => (isUnknownCountryCode(code) ? renderCountryCell() : getCountryName(code))
  // const getRegionDisplay = (r) => (r == null || r === '' ? UNKNOWN_LABEL : r)

  // useEffect(() => {
  //   if (selectedFilterCountry.length === 0 && selectedFilterRegion.length > 0) {
  //     setSelectedFilterRegion([])
  //   }
  // }, [selectedFilterCountry, selectedFilterRegion.length])

  useEffect(() => {
    if (props.deviceModels.length === 0) {
      props.actions.loadModels()
    }

    if (props.geographicalConnectionCountries.length === 0) {
      props.actions.loadConnectedDevicesCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return
    const t = window.setTimeout(() => {
      setSelectedPage(1)
      handleLoadTableData(true)
    })
    return () => clearTimeout(t)
  }, [selectedFilterCountry, selectedFilterModel, selectedFilterRegion, startDateFilter, endDateFilter, orderBy, shouldApplyFilters])

  useEffect(() => {
    // const unknownTotal = (props.geographicalConnectionSummaryData || [])
    //   .filter((d) => isUnknownCountryCode(d.country))
    //   .reduce((sum, d) => sum + (Number(d.count) || 0), 0)

    // const knownRows = (props.geographicalConnectionSummaryData || [])
    //   .filter((d) => !isUnknownCountryCode(d.country))
    //   .map((device, index) => ({
    //     index,
    //     count: device.count,
    //     country: getCountryDisplay(device.country),
    //     region: device.region,
    //   }))

    // const rows =
    //   unknownTotal > 0
    //     ? [
    //         ...knownRows,
    //         {
    //           index: 'unknown',
    //           count: unknownTotal,
    //           country: renderCountryCell(),
    //           region: '',
    //         },
    //       ]
    //     : knownRows

    const tableData = props.geographicalConnectionSummaryData.map((device, index) => ({
      index,
      count: device.count,
      country: getCountryDisplay(device.country),
      region: device.region,
    }))

    setGeographicalConnectionSummaryData(tableData)
    setVisibleCount(PAGE_SIZE)
  }, [props.geographicalConnectionSummaryData])

  const renderTooltipText = () => {
    return (
      <div className="tooltip-text">
        <p className="tooltip-body">{t('reports.unknown_country_message')}</p>
      </div>
    )
  }

  const renderCountryCell = () => {
    return (
      <div className="info-icon">
        {UNKNOWN_LABEL} <TooltipedIcon icon={<InformationIcon />} tooltipText={renderTooltipText()} orientation={isMobile ? 'left' : 'right'} />
      </div>
    )
  }

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.geographicalConnectionCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (noPing) {
      queryParams = { ...queryParams, noPing: true }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
    }

    props.actions.loadGeographicalConnectionSummaryData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel([])
    setSelectedFilterRegion([])
    setSelectedFilterCountry([])
    setStartDateFilter(null)
    setEndDateFilter(null)
    setNoPing(false)
    setVisibleCount(PAGE_SIZE)
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
    setVisibleCount(PAGE_SIZE)
  }

  const handleDownload = () => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
    }

    props.actions.downloadGeographicalConnectionData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `connected-devices_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const renderChartSection = () => {
    return (
      <div className="chart-section">
        <div className="chart-title">{t('reports.geographical_reports_map')}</div>
        {/* <div className="disclaimer-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="disclaimer-wrapper">
            <div className="title">{t('reports.geographical_chart_disclaimer')}</div>
          </div>
        </div> */}
        <GeographicalConnectionChart data={props.geographicalConnectionSummaryData || []} />
      </div>
    )
  }

  const renderDevicesTable = () => {
    const rowsToRender = geographicalConnectionSummaryData.slice(0, visibleCount)

    if (!props.isLoading && !isFirstLoad && geographicalConnectionSummaryData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile && rowsToRender.length > 0) {
      return (
        <>
          <GenericTable
            data={rowsToRender}
            headers={[
              {
                title: t('country'),
                orderKey: 'country',
              },
              {
                title: t('region'),
                orderKey: 'region',
              },
              {
                title: t('count'),
                orderKey: 'total',
              },
              {
                title: '',
              },
              {
                title: '',
              },
              {
                title: '',
              },
            ]}
            keys={['country', 'region', 'count', '', '', '']}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={props.isLoading}
          />
          {visibleCount < geographicalConnectionSummaryData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, geographicalConnectionSummaryData.length))}
                disabled={props.isLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }

    if (isMobile) {
      return (
        <>
          {geographicalConnectionSummaryData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleSort(sortOption)
                }}
              />
            </div>
          )}

          {rowsToRender.map((device, index) => (
            <div key={index} className="card" onClick={() => setExpandedDevice(index)}>
              <div className={'card-item' + (expandedDevice !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('country')}</div>
                <div className={'card-item-body' + (expandedDevice !== index ? ' align-center' : '')}>
                  <div>{device.country}</div>
                </div>
              </div>
              {expandedDevice === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('region')}</div>
                    <div className="card-item-body">{device.region}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('count')}</div>
                    <div className="card-item-body">{device.count}</div>
                  </div>
                </>
              )}
            </div>
          ))}
          {visibleCount < geographicalConnectionSummaryData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, geographicalConnectionSummaryData.length))}
                disabled={props.isLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderDevicesTable()}
    </div>
  )

  return (
    <div className="internal-reports-section geographical-connection">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            (startDateFilter && endDateFilter) || selectedFilterModel.length > 0 || selectedFilterRegion.length > 0 || selectedFilterCountry.length > 0
          }
          counterBadge={selectedFilterModel.length + selectedFilterRegion.length + selectedFilterCountry.length + (startDateFilter && endDateFilter ? 1 : 0)}
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.geographical_chart_disclaimer')}</div>
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!hasTable && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('reports.last_24_hours'), hours: 24, onClick: () => setNoPing(false) },
                { label: t('1_month'), months: 1, onClick: () => setNoPing(false) },
                { label: t('3_months'), months: 3, onClick: () => setNoPing(false) },
                { label: t('6_months'), months: 6, onClick: () => setNoPing(false) },
                { label: t('12_months'), months: 12, onClick: () => setNoPing(false) },
                { label: t('reports.no_ping_last_7_days'), days: 7, onClick: () => setNoPing(true) },
              ],
            },
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.deviceModels,
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.geographicalConnectionCountries.map((c) => c.name),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ reports, shared }) {
  const rawModels = [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])]

  return {
    geographicalConnectionSummaryData: reports?.geographicalConnectionSummaryData || [],
    geographicalConnectionCountries: reports?.connectedDevicesCountries || [],
    deviceModels: sortModelsAlphabetically(rawModels),
    reportsRegions: shared?.regions || [],
    isLoading: reports?.isLoading,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadGeographicalConnectionSummaryData,
        loadConnectedDevicesCountries,
        downloadGeographicalConnectionData,
        clearReportsMessages,
        loadRegions,
        loadModels,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(GeographicalConnectionReports)

